import { z } from "zod";
import { uuid } from "./common";

/*
 * Ayurveda edition A4: packages. A package plan is a set of services with quantities sold at one tax-inclusive price
 * (e.g. "Back care 14 days"). Selling one issues an invoice whose lines split the price across the services in
 * proportion to their list prices, so every line carries its own GST. Units are used by completed therapy sessions
 * (automatically) or marked used by hand; cancelling credits the unused units at the package rate.
 */

const optText = (max: number) => z.string().trim().max(max).nullish().transform((v) => v || null);
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use a date like 2026-10-12");

export const packagePlanInput = z.object({
  code: z.string().trim().toUpperCase().regex(/^[A-Z0-9][A-Z0-9_-]*$/, "Letters, numbers, - and _").max(20),
  name: z.string().trim().min(1).max(120),
  description: optText(2000),
  priceMinor: z.number().int().min(0).max(100_000_000_00),
  validityDays: z.number().int().min(1).max(730),
  isActive: z.boolean().default(true),
  items: z
    .array(z.object({ serviceId: uuid, quantity: z.number().int().min(1).max(365) }))
    .min(1)
    .max(20)
    .refine((xs) => new Set(xs.map((x) => x.serviceId)).size === xs.length, "Each service once; set its quantity instead"),
});
export type PackagePlanInput = z.input<typeof packagePlanInput>;

export interface PackagePlan {
  id: string;
  code: string;
  name: string;
  description: string | null;
  priceMinor: number;
  validityDays: number;
  isActive: boolean;
  /** Sum of the items at today's list prices (what the services would cost one by one). */
  listValueMinor: number | null;
  items: { serviceId: string; serviceName: string; quantity: number; unitPriceMinor: number | null }[];
  version: number;
}

export const sellPackageInput = z.object({
  planId: uuid,
  branchId: uuid,
  startsOn: isoDate.nullish().transform((v) => v ?? null),
  notes: optText(1000),
});

export const usePackageInput = z.object({ itemId: uuid, note: optText(300) });
export const reverseUsageInput = z.object({ reason: z.string().trim().min(3).max(300) });
export const cancelPackageInput = z.object({ version: z.number().int().min(1), reason: z.string().trim().min(3).max(300) });

export type PackageStatus = "active" | "completed" | "cancelled" | "expired";

export interface PatientPackage {
  id: string;
  patientId: string;
  branchId: string;
  name: string;
  priceMinor: number;
  startsOn: string;
  validUntil: string;
  /** "expired" = still active in the books but past its validity date. */
  status: PackageStatus;
  invoice: { id: string; number: string | null; status: string; balanceMinor: number };
  creditNote: { id: string; number: string | null; totalMinor: number } | null;
  cancelReason: string | null;
  notes: string | null;
  items: { id: string; serviceId: string; description: string; quantity: number; used: number; valueMinor: number }[];
  usages: { id: string; itemId: string; description: string; usedAt: string; note: string | null; therapySessionId: string | null; reversedAt: string | null; reversalReason: string | null }[];
  /** Value of units not used yet at the package rate (what cancelling would credit). */
  unusedValueMinor: number;
  version: number;
}

/** Package price split across items in proportion to their list values (largest remainder, exact to the paisa). */
export function splitPackagePrice(priceMinor: number, weights: number[]): number[] {
  if (!weights.length) return [];
  const total = weights.reduce((a, w) => a + Math.max(0, w), 0);
  const raw = weights.map((w) => (total > 0 ? (priceMinor * Math.max(0, w)) / total : priceMinor / weights.length));
  const out = raw.map(Math.floor);
  let left = priceMinor - out.reduce((a, x) => a + x, 0);
  const order = raw.map((r, i) => ({ i, frac: r - Math.floor(r) })).sort((a, b) => b.frac - a.frac || a.i - b.i);
  for (const { i } of order) {
    if (left <= 0) break;
    out[i]! += 1;
    left -= 1;
  }
  return out;
}

/** Value of `unused` of `quantity` units of a line worth `valueMinor` (whole line when nothing was used). */
export const unusedValue = (valueMinor: number, quantity: number, used: number) =>
  used <= 0 ? valueMinor : used >= quantity ? 0 : Math.floor((valueMinor * (quantity - used)) / quantity);
